import { useEffect, useState } from "react";
import { Plus, Trash2, Save, ChevronRight, Table2, RefreshCw, FileText, AlertTriangle, Radio, ChevronUp, ChevronDown, ClipboardPaste, LayoutGrid, Palette, Star, Archive } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { DIVISION_COLORS } from "@/lib/divisionTheme";
import { RichTextEditor } from "@/components/RichText";
import { TableEditor } from "@/components/admin/TableEditor";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { FileUpload } from "@/components/admin/FileUpload";
import { ShareToFacebook } from "@/components/admin/ShareToFacebook";
import { AutoClearPanel } from "@/components/admin/AutoClearPanel";

const input = "w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4]";

const DivisionListItem = ({ d, active, onOpen }) => (
  <button
    onClick={onOpen}
    data-testid={`admin-division-${d.slug}`}
    className={`w-full flex items-start justify-between gap-2 text-left text-sm rounded-md px-3 py-2 transition-colors ${
      active ? "bg-[#1E63C4] text-white" : "hover:bg-gray-100"
    }`}
  >
    <span className="min-w-0 break-words">
      {d.name}
      <span
        data-testid={`admin-division-tag-${d.slug}`}
        className={`ml-1.5 inline-block align-middle text-[10px] font-semibold uppercase tracking-wide rounded-full px-1.5 py-0.5 ${
          d.archived ? "bg-gray-200 text-gray-600" : "bg-green-100 text-green-700"
        }`}
      >
        {d.archived ? "Previous" : "Current"}
      </span>
    </span>
    <ChevronRight className="h-4 w-4 shrink-0 mt-0.5" />
  </button>
);

const ALL_REPORTS = [
  { key: "team_standings", label: "Team Standings" },
  { key: "player_standings", label: "Player Standings" },
  { key: "players_list", label: "Players List" },
  { key: "players_by_team", label: "Players by Team" },
  { key: "schedule", label: "Schedule" },
];

export function DivisionsManager() {
  const [divisions, setDivisions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tables, setTables] = useState([]);
  const [newName, setNewName] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [impPaste, setImpPaste] = useState("");
  const [wantPaste, setWantPaste] = useState("");
  const [showImpPaste, setShowImpPaste] = useState(false);
  const [showWantPaste, setShowWantPaste] = useState(false);

  const loadDivisions = async () => {
    const { data } = await api.get("/divisions");
    setDivisions(data);
    return data;
  };

  useEffect(() => {
    api.get("/divisions").then((r) => setDivisions(r.data)).catch(() => {});
  }, []);

  const openDivision = async (slug) => {
    const { data } = await api.get(`/divisions/${slug}`);
    setSelected(data);
    setTables(data.tables || []);
  };

  const reloadTables = async () => {
    if (!selected) return;
    const { data } = await api.get(`/divisions/${selected.slug}`);
    setSelected(data);
    setTables(data.tables || []);
  };

  const createDivision = async () => {
    if (!newName.trim()) return;
    try {
      const { data } = await api.post("/admin/divisions", { name: newName.trim(), order: divisions.length + 1 });
      setNewName("");
      await loadDivisions();
      openDivision(data.slug);
      toast.success("Division created");
    } catch {
      toast.error("Could not create division");
    }
  };

  const divisionPayload = () => ({
    name: selected.name,
    slug: selected.slug,
    night: selected.night || "",
    description: selected.description || "",
    info: selected.info || "",
    order: selected.order || 0,
    fargo_division_id: selected.fargo_division_id || "",
    fargo_reports: selected.fargo_reports || [],
    archived: !!selected.archived,
    season: selected.season || "",
    champion_team: selected.champion_team || "",
    champion_players: selected.champion_players || "",
    champion_image: selected.champion_image || "",
    documents: selected.documents || [],
    weekly_highlights_enabled: selected.weekly_highlights_enabled !== false,
    show_wanted: selected.show_wanted !== false,
    show_improvement: selected.show_improvement !== false,
    total_weeks: parseInt(selected.total_weeks, 10) || 0,
    live_stream_teams: selected.live_stream_teams || "",
    wanted_player: selected.wanted_player || "",
    wanted_image: selected.wanted_image || "",
    live_stream_image: selected.live_stream_image || "",
    improvement_giveaways: selected.improvement_giveaways || "",
    wanted_giveaways: selected.wanted_giveaways || "",
    improvement_giveaways_rows: selected.improvement_giveaways_rows || [],
    wanted_giveaways_rows: selected.wanted_giveaways_rows || [],
    card_buttons: selected.card_buttons || { schedule: true, standings: true, players: true, teams: true, rules: true },
    color: selected.color || "",
    review: selected.review || "",
    review_date: selected.review_date || "",
  });

  const saveDivision = async () => {
    try {
      await api.put(`/admin/divisions/${selected.id}`, divisionPayload());
      await loadDivisions();
      toast.success("Division details saved");
    } catch {
      toast.error("Save failed");
    }
  };

  const syncNow = async () => {
    if (!selected.fargo_division_id) {
      toast.error("Add a FargoRate Division ID first, then Save.");
      return;
    }
    setSyncing(true);
    try {
      await api.put(`/admin/divisions/${selected.id}`, divisionPayload());
      const { data } = await api.post(`/admin/divisions/${selected.id}/sync-fargo`);
      toast.success(`Synced from FargoRate (${(data.synced || []).length} reports)`);
      await reloadTables();
      await loadDivisions();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const deleteDivision = async () => {
    if (!window.confirm(`Delete division "${selected.name}" and all its tables?`)) return;
    await api.delete(`/admin/divisions/${selected.id}`);
    setSelected(null);
    setTables([]);
    await loadDivisions();
    toast.success("Division deleted");
  };

  const addTable = async () => {
    const { data } = await api.post(`/admin/divisions/${selected.id}/tables`, {
      title: "New Table",
      kind: "general",
      columns: ["Column 1", "Column 2"],
      rows: [["", ""]],
      order: tables.length + 1,
    });
    setTables([...tables, data]);
    toast.success("Table added");
  };

  const setSel = (k, v) => setSelected((s) => ({ ...s, [k]: v }));
  const cardBtns = () => selected?.card_buttons || { schedule: true, standings: true, players: true, teams: true, rules: true };
  const setCardBtn = (key, val) => setSelected((s) => ({ ...s, card_buttons: { ...cardBtns(), [key]: val } }));

  const impRows = () => selected.improvement_giveaways_rows || [];
  const addImpRow = () => setSel("improvement_giveaways_rows", [...impRows(), { id: crypto.randomUUID(), name: "", prev_avg: "", wkly_avg: "" }]);
  const updImpRow = (i, k, v) => setSel("improvement_giveaways_rows", impRows().map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const rmImpRow = (i) => setSel("improvement_giveaways_rows", impRows().filter((_, idx) => idx !== i));

  const wantRows = () => selected.wanted_giveaways_rows || [];
  const addWantRow = () => setSel("wanted_giveaways_rows", [...wantRows(), { id: crypto.randomUUID(), name: "", value: "" }]);
  const updWantRow = (i, k, v) => setSel("wanted_giveaways_rows", wantRows().map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const rmWantRow = (i) => setSel("wanted_giveaways_rows", wantRows().filter((_, idx) => idx !== i));

  // Bulk paste: one player per line. Trailing tokens are the numbers, the rest is the name.
  const parseImpPaste = () => {
    const rows = impPaste.split("\n").map((l) => l.trim()).filter(Boolean).map((line) => {
      const t = line.split(/\s+/);
      if (t.length < 3) return { id: crypto.randomUUID(), name: line, prev_avg: "", wkly_avg: "" };
      return { id: crypto.randomUUID(), name: t.slice(0, -2).join(" "), prev_avg: t[t.length - 2], wkly_avg: t[t.length - 1] };
    });
    if (!rows.length) { toast.error("Nothing to add — paste one player per line."); return; }
    setSel("improvement_giveaways_rows", rows);
    setImpPaste(""); setShowImpPaste(false);
    toast.success(`Added ${rows.length} player${rows.length === 1 ? "" : "s"}`);
  };

  const parseWantPaste = () => {
    const rows = wantPaste.split("\n").map((l) => l.trim()).filter(Boolean).map((line) => {
      const t = line.split(/\s+/);
      if (t.length < 2) return { id: crypto.randomUUID(), name: line, value: "" };
      return { id: crypto.randomUUID(), name: t.slice(0, -1).join(" "), value: t[t.length - 1] };
    });
    if (!rows.length) { toast.error("Nothing to add — paste one player per line."); return; }
    setSel("wanted_giveaways_rows", rows);
    setWantPaste(""); setShowWantPaste(false);
    toast.success(`Added ${rows.length} player${rows.length === 1 ? "" : "s"}`);
  };

  const selectedReports = selected && selected.fargo_reports && selected.fargo_reports.length
    ? selected.fargo_reports
    : ALL_REPORTS.map((r) => r.key);

  const toggleReport = (key) => {
    const next = selectedReports.includes(key)
      ? selectedReports.filter((k) => k !== key)
      : [...selectedReports, key];
    setSel("fargo_reports", next);
  };

  return (
    <div className="space-y-6">
    <AutoClearPanel refreshKey={divisions} />
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr]" data-testid="divisions-manager">
      {/* Sidebar */}
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-4 h-fit">
        <h3 className="font-heading font-bold mb-3">Divisions</h3>
        <div className="space-y-4 mb-4">
          {[
            { key: "current", title: "Current", list: divisions.filter((d) => !d.archived) },
            { key: "previous", title: "Previous seasons", list: divisions.filter((d) => d.archived) },
          ].map((g) => g.list.length > 0 && (
            <div key={g.key} className="space-y-1" data-testid={`admin-division-group-${g.key}`}>
              <div className="px-3 text-[10px] font-semibold uppercase tracking-wider text-gray-400">{g.title}</div>
              {g.list.map((d) => (
                <DivisionListItem key={d.id} d={d} active={selected?.id === d.id} onOpen={() => openDivision(d.slug)} />
              ))}
            </div>
          ))}
        </div>
        <div className="border-t border-[#E5E7EB] pt-3">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New division name"
            data-testid="new-division-input"
            className="w-full rounded-md border border-[#E5E7EB] px-3 py-2 text-sm mb-2"
          />
          <button
            onClick={createDivision}
            data-testid="create-division-btn"
            className="w-full inline-flex items-center justify-center gap-1 bg-[#E11D2A] text-white rounded-md px-3 py-2 text-sm font-semibold hover:bg-[#c41320]"
          >
            <Plus className="h-4 w-4" /> Add division
          </button>
        </div>
      </div>

      {/* Editor */}
      <div>
        {!selected ? (
          <div className="bg-white rounded-lg border border-[#E5E7EB] p-10 text-center text-[#555555]">
            <Table2 className="h-8 w-8 mx-auto mb-3 text-gray-300" />
            Select a division on the left, or create one to start adding standings, stats and schedules.
          </div>
        ) : (
          <div className="space-y-6">
            {selected.archived && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="archived-division-notice">
                <Archive className="h-4 w-4 mt-0.5 shrink-0" />
                <span><strong>Previous season</strong>, not shown on the home page. Changes here won't affect the current division cards.</span>
              </div>
            )}
            <div className="bg-white rounded-lg border border-[#E5E7EB] p-6 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-[#333333] mb-1">Division name</label>
                  <input className={input} value={selected.name} onChange={(e) => setSel("name", e.target.value)} data-testid="edit-division-name" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#333333] mb-1">Night / time</label>
                  <input className={input} value={selected.night || ""} onChange={(e) => setSel("night", e.target.value)} placeholder="e.g. Sundays 6:00 PM" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#333333] mb-1">Short description</label>
                <input className={input} value={selected.description || ""} onChange={(e) => setSel("description", e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#333333] mb-1">Info / rules</label>
                <RichTextEditor key={selected.id} value={selected.info || ""} onChange={(v) => setSel("info", v)} testid="division-info-editor" placeholder="Info / rules — type or paste formatted text" />
              </div>

              <div className="grid gap-4 sm:grid-cols-2 border-t border-[#E5E7EB] pt-4">
                <div>
                  <label className="block text-sm font-medium text-[#333333] mb-1">Season label (optional)</label>
                  <input className={input} value={selected.season || ""} onChange={(e) => setSel("season", e.target.value)} placeholder="e.g. Spring 2026" data-testid="season-input" />
                </div>
                <div className="flex items-end">
                  <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="archive-toggle">
                    <input type="checkbox" checked={!!selected.archived} onChange={(e) => setSel("archived", e.target.checked)} className="h-4 w-4 mt-0.5 accent-[#1E63C4]" />
                    <span>Move to <strong>Previous</strong> (completed season — freezes stats &amp; stops auto-sync)</span>
                  </label>
                </div>
              </div>

              {/* Champion (shown for completed/previous seasons) */}
              <div className="border-t border-[#E5E7EB] pt-4 space-y-4" data-testid="champion-panel">
                <div className="flex items-center gap-2">
                  <span className="text-base">🏆</span>
                  <h4 className="font-heading font-bold">Season Champion <span className="font-normal text-xs text-[#555555]">(shown on Past Champions when this division is in Previous)</span></h4>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-[#333333] mb-1">Champion team / winner(s)</label>
                    <textarea className={`${input} resize-y`} rows={4} value={selected.champion_team || ""} onChange={(e) => setSel("champion_team", e.target.value)} placeholder={"e.g. Blues Cues\n\nFor a singles league, enter one winner per line:\nJane Doe\nJohn Smith"} data-testid="champion-team-input" />
                    <p className="text-xs text-[#888888] mt-1">Tip: for a singles league, put each winner on its own line.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#333333] mb-1">Player names</label>
                    <input className={input} value={selected.champion_players || ""} onChange={(e) => setSel("champion_players", e.target.value)} placeholder="e.g. Jane Doe, John Smith & Sam Lee" data-testid="champion-players-input" />
                  </div>
                </div>
                <ImageUpload label="Champion team image / banner" value={selected.champion_image} onChange={(v) => setSel("champion_image", v)} testid="champion-image" />
                <ShareToFacebook
                  image={selected.champion_image}
                  defaultCaption={[
                    [selected.name, selected.season, "Champions"].filter(Boolean).join(" "),
                    selected.champion_team,
                    selected.champion_players ? `Players: ${selected.champion_players}` : "",
                  ].filter(Boolean).join("\n\n")}
                  source="champion"
                  testid="fb-champion"
                />
              </div>

              {/* Documents (Rules, Bylaws, PDFs) */}
              <div className="border-t border-[#E5E7EB] pt-4 space-y-3" data-testid="documents-panel">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-[#1E63C4]" />
                    <h4 className="font-heading font-bold">Documents <span className="font-normal text-xs text-[#555555]">(Rules, Bylaws — PDF files shown on the division page)</span></h4>
                  </div>
                  <button
                    onClick={() => setSel("documents", [...(selected.documents || []), { id: crypto.randomUUID(), title: "", url: "" }])}
                    data-testid="add-document-btn"
                    className="inline-flex items-center gap-1 text-sm text-[#1E63C4] font-semibold"
                  >
                    <Plus className="h-4 w-4" /> Add document
                  </button>
                </div>
                {(selected.documents || []).map((doc, i) => (
                  <div key={doc.id || i} className="flex flex-wrap items-end gap-3 border border-[#E5E7EB] rounded-md p-3" data-testid={`document-row-${i}`}>
                    <div className="flex flex-col gap-1 pb-1">
                      <button
                        type="button"
                        onClick={() => {
                          if (i === 0) return;
                          const docs = [...selected.documents];
                          [docs[i - 1], docs[i]] = [docs[i], docs[i - 1]];
                          setSel("documents", docs);
                        }}
                        disabled={i === 0}
                        title="Move up"
                        aria-label="Move document up"
                        data-testid={`document-move-up-${i}`}
                        className="text-[#555555] hover:text-[#1E63C4] disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronUp className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (i >= selected.documents.length - 1) return;
                          const docs = [...selected.documents];
                          [docs[i + 1], docs[i]] = [docs[i], docs[i + 1]];
                          setSel("documents", docs);
                        }}
                        disabled={i >= (selected.documents || []).length - 1}
                        title="Move down"
                        aria-label="Move document down"
                        data-testid={`document-move-down-${i}`}
                        className="text-[#555555] hover:text-[#1E63C4] disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronDown className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="flex-1 min-w-[180px]">
                      <label className="block text-xs font-medium text-[#555555] mb-1">Title</label>
                      <input
                        className={input}
                        placeholder="e.g. League Rules / Bylaws"
                        value={doc.title}
                        onChange={(e) => setSel("documents", selected.documents.map((d, idx) => (idx === i ? { ...d, title: e.target.value } : d)))}
                        data-testid={`document-title-${i}`}
                      />
                    </div>
                    <FileUpload
                      label="PDF file"
                      value={doc.url}
                      onChange={(v) => setSel("documents", selected.documents.map((d, idx) => (idx === i ? { ...d, url: v } : d)))}
                      testid={`document-file-${i}`}
                    />
                    <button
                      onClick={() => setSel("documents", selected.documents.filter((_, idx) => idx !== i))}
                      className="text-red-500 pb-2"
                      data-testid={`remove-document-${i}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Division review (rich text) */}
              <div className="border-t border-[#E5E7EB] pt-4 space-y-3" data-testid="division-review-panel">
                <div className="flex items-center gap-2">
                  <Star className="h-4 w-4 text-[#1E63C4]" />
                  <h4 className="font-heading font-bold">Division review <span className="font-normal text-xs text-[#555555]">(a write-up shown near the top of this division&apos;s page — type or paste formatted text)</span></h4>
                </div>
                <RichTextEditor
                  key={selected.id}
                  value={selected.review || ""}
                  onChange={(html) => setSel("review", html)}
                  testid="division-review-editor"
                />
                <div className="flex items-center gap-3 flex-wrap">
                  <label className="text-sm font-medium text-[#333333]">Review date <span className="text-xs text-[#555555]">(shown as the &quot;Review&quot; tag on the cards)</span></label>
                  <input
                    type="date"
                    className={`${input} max-w-[200px]`}
                    value={selected.review_date || ""}
                    onChange={(e) => setSel("review_date", e.target.value)}
                    data-testid="division-review-date"
                  />
                  {selected.review_date && (
                    <button type="button" onClick={() => setSel("review_date", "")} className="text-xs text-[#999999] hover:text-red-500" data-testid="division-review-date-clear">Clear date</button>
                  )}
                </div>
              </div>

              {/* Division accent colour */}
              <div className="border-t border-[#E5E7EB] pt-4 space-y-3" data-testid="division-color-panel">
                <div className="flex items-center gap-2">
                  <Palette className="h-4 w-4 text-[#1E63C4]" />
                  <h4 className="font-heading font-bold">Division colour <span className="font-normal text-xs text-[#555555]">(a subtle accent for this division&apos;s card &amp; page — headers, buttons &amp; tints)</span></h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  {DIVISION_COLORS.map((c) => {
                    const active = (selected.color || "blue") === c.key;
                    return (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => setSel("color", c.key)}
                        data-testid={`division-color-${c.key}`}
                        title={c.label}
                        className={`flex items-center gap-2 rounded-full border pl-1.5 pr-3 py-1.5 text-sm font-medium transition-all ${active ? "border-[#111111] shadow-sm" : "border-[#E5E7EB] hover:border-[#999999]"}`}
                      >
                        <span className="h-5 w-5 rounded-full ring-1 ring-black/10" style={{ backgroundColor: c.hex }} />
                        {c.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Home card buttons */}
              <div className="border-t border-[#E5E7EB] pt-4 space-y-3" data-testid="card-buttons-panel">
                <div className="flex items-center gap-2">
                  <LayoutGrid className="h-4 w-4 text-[#1E63C4]" />
                  <h4 className="font-heading font-bold">Home card buttons <span className="font-normal text-xs text-[#555555]">(choose which action buttons show on this division&apos;s Home-page card)</span></h4>
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {[
                    ["schedule", "Schedule"],
                    ["standings", "Standings"],
                    ["players", "Player Stats"],
                    ["teams", "Team Stats"],
                    ["rules", "Rules"],
                  ].map(([key, label]) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer bg-[#F9FAF8] border border-[#E5E7EB] rounded-md px-3 py-2" data-testid={`card-btn-toggle-label-${key}`}>
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-[#1E63C4]"
                        checked={cardBtns()[key] !== false}
                        onChange={(e) => setCardBtn(key, e.target.checked)}
                        data-testid={`card-btn-toggle-${key}`}
                      />
                      <span className="text-sm text-[#333333] font-medium">{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="border-t border-[#E5E7EB] pt-4 space-y-4" data-testid="highlights-panel">
                <div className="flex items-center gap-2">
                  <Radio className="h-4 w-4 text-[#E11D2A]" />
                  <h4 className="font-heading font-bold">Weekly Highlights <span className="font-normal text-xs text-[#555555]">(shown near the top of this division&apos;s page while it&apos;s Current. Leave blank to hide.)</span></h4>
                </div>
                <label className="flex items-start gap-2 cursor-pointer bg-[#F9FAF8] border border-[#E5E7EB] rounded-md p-3" data-testid="weekly-highlights-toggle-label">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 accent-[#1E63C4]"
                    checked={selected.weekly_highlights_enabled !== false}
                    onChange={(e) => setSel("weekly_highlights_enabled", e.target.checked)}
                    data-testid="weekly-highlights-toggle"
                  />
                  <span className="text-sm text-[#333333]">
                    <span className="font-semibold">Use weekly highlights on this division</span>
                    <span className="block text-xs text-[#555555]">When off, the Live Stream / WANTED / giveaway fields are hidden and are never auto-filled with &quot;Coming Soon&quot;. Turn this off for divisions that don&apos;t run weekly highlights.</span>
                  </span>
                </label>
                {selected.weekly_highlights_enabled !== false && (
                  <div className="grid gap-3 sm:grid-cols-2" data-testid="section-visibility-toggles">
                    {[
                      ["show_wanted", "show-wanted-toggle", "Show WANTED", "WANTED poster, This Week's WANTED, WANTED Giveaways list and the WANTED line on division cards."],
                      ["show_improvement", "show-improvement-toggle", "Show Player Improvement", "The Player Improvement Giveaways list on the division page."],
                    ].map(([key, testid, label, help]) => (
                      <label key={key} className="flex items-start gap-2 cursor-pointer bg-[#F9FAF8] border border-[#E5E7EB] rounded-md p-3">
                        <input
                          type="checkbox"
                          className="mt-0.5 h-4 w-4 accent-[#1E63C4]"
                          checked={selected[key] !== false}
                          onChange={(e) => setSel(key, e.target.checked)}
                          data-testid={testid}
                        />
                        <span className="text-sm text-[#333333]">
                          <span className="font-semibold">{label}</span>
                          <span className="block text-xs text-[#555555]">{help} When off, it&apos;s hidden everywhere on the site; its content is kept.</span>
                        </span>
                      </label>
                    ))}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-[#333333] mb-1">Total weeks in season <span className="text-xs text-[#555555]">(set this if you add the schedule week-by-week, e.g. Monday Singles. Leave 0 to auto-detect from a full schedule — the current week is always calculated from the schedule dates.)</span></label>
                  <input type="number" min="0" className={`${input} max-w-[160px]`} value={selected.total_weeks || ""} onChange={(e) => setSel("total_weeks", parseInt(e.target.value, 10) || 0)} placeholder="0 = auto" data-testid="total-weeks-input" />
                </div>
                {selected.weekly_highlights_enabled !== false && (<>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-[#333333] mb-1">Live Stream Match Teams</label>
                    <input className={input} value={selected.live_stream_teams || ""} onChange={(e) => setSel("live_stream_teams", e.target.value)} placeholder="e.g. Lettuce Win vs. Classic Rock" data-testid="live-stream-teams-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#333333] mb-1">WANTED Player</label>
                    <input className={input} value={selected.wanted_player || ""} onChange={(e) => setSel("wanted_player", e.target.value)} placeholder="e.g. Terry Wood" data-testid="wanted-player-input" />
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <ImageUpload label="Live Stream Match poster" value={selected.live_stream_image} onChange={(v) => setSel("live_stream_image", v)} testid="live-stream-image" />
                  <ImageUpload label="WANTED poster" value={selected.wanted_image} onChange={(v) => setSel("wanted_image", v)} testid="wanted-image" />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <ShareToFacebook
                    image={selected.live_stream_image}
                    defaultCaption={[
                      selected.live_stream_teams ? `Live Stream Match: ${selected.live_stream_teams}` : `${selected.name} — Live Stream Match`,
                    ].filter(Boolean).join("\n\n")}
                    source="live-stream"
                    testid="fb-live-stream"
                  />
                  <ShareToFacebook
                    image={selected.wanted_image}
                    defaultCaption={[
                      selected.wanted_player ? `WANTED: ${selected.wanted_player}` : `${selected.name} — WANTED Player of the Week`,
                    ].filter(Boolean).join("\n\n")}
                    source="wanted"
                    testid="fb-wanted"
                  />
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div data-testid="improvement-rows-editor">
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="block text-sm font-medium text-[#333333]">Player Improvement Giveaways Qualifiers</label>
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => setShowImpPaste((v) => !v)} data-testid="paste-improvement-toggle" className="inline-flex items-center gap-1 text-xs font-semibold text-[#555555] hover:text-[#1E63C4]"><ClipboardPaste className="h-3.5 w-3.5" /> Paste</button>
                        <button type="button" onClick={addImpRow} data-testid="add-improvement-row" className="inline-flex items-center gap-1 text-xs font-semibold text-[#1E63C4] hover:underline"><Plus className="h-3.5 w-3.5" /> Add</button>
                      </div>
                    </div>
                    {showImpPaste && (
                      <div className="mb-2 rounded-md border border-[#1E63C4]/30 bg-[#1E63C4]/5 p-2.5" data-testid="paste-improvement-box">
                        <p className="text-xs text-[#555555] mb-1.5">One player per line: <code className="bg-white px-1 rounded">FirstName LastName CurAvg WkAvg</code></p>
                        <textarea className={input} rows={4} value={impPaste} onChange={(e) => setImpPaste(e.target.value)} placeholder={"Jane Doe 3.2 4.1\nJohn Smith 2.8 3.5"} data-testid="paste-improvement-input" />
                        <div className="flex items-center gap-2 mt-1.5">
                          <button type="button" onClick={parseImpPaste} data-testid="paste-improvement-apply" className="inline-flex items-center gap-1 bg-[#1E63C4] text-white rounded-md px-3 py-1.5 text-xs font-semibold hover:bg-[#174e9c]">Replace rows with list</button>
                          <span className="text-xs text-[#999999]">Replaces all qualifiers below.</span>
                        </div>
                      </div>
                    )}
                    {impRows().length > 0 && (
                      <div className="grid grid-cols-[1fr_5rem_5rem_auto] gap-1.5 mb-1 text-[11px] font-semibold text-[#555555] uppercase tracking-wide">
                        <span>Name</span><span>Prev Avg</span><span>Wkly Avg</span><span />
                      </div>
                    )}
                    <div className="space-y-1.5">
                      {impRows().map((r, i) => (
                        <div key={r.id || i} className="grid grid-cols-[1fr_5rem_5rem_auto] gap-1.5 items-center" data-testid={`improvement-row-${i}`}>
                          <input className={input} value={r.name} onChange={(e) => updImpRow(i, "name", e.target.value)} placeholder="Name" data-testid={`improvement-name-${i}`} />
                          <input className={input} value={r.prev_avg} onChange={(e) => updImpRow(i, "prev_avg", e.target.value)} placeholder="—" data-testid={`improvement-prev-${i}`} />
                          <input className={input} value={r.wkly_avg} onChange={(e) => updImpRow(i, "wkly_avg", e.target.value)} placeholder="—" data-testid={`improvement-wkly-${i}`} />
                          <button type="button" onClick={() => rmImpRow(i)} className="text-red-500 p-1" data-testid={`remove-improvement-${i}`}><Trash2 className="h-4 w-4" /></button>
                        </div>
                      ))}
                      {impRows().length === 0 && <p className="text-xs text-[#999999]">No qualifiers yet. Click Add.</p>}
                    </div>
                  </div>
                  <div data-testid="wanted-rows-editor">
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-[#333333]">WANTED Giveaways Qualifiers</label>
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => setShowWantPaste((v) => !v)} data-testid="paste-wanted-toggle" className="inline-flex items-center gap-1 text-xs font-semibold text-[#555555] hover:text-[#1E63C4]"><ClipboardPaste className="h-3.5 w-3.5" /> Paste</button>
                        <button type="button" onClick={addWantRow} data-testid="add-wanted-row" className="inline-flex items-center gap-1 text-xs font-semibold text-[#1E63C4] hover:underline"><Plus className="h-3.5 w-3.5" /> Add</button>
                      </div>
                    </div>
                    <p className="text-xs text-[#555555] mb-1.5">The first row is shown separately as the featured <span className="font-semibold">WANTED Player</span>.</p>
                    {showWantPaste && (
                      <div className="mb-2 rounded-md border border-[#1E63C4]/30 bg-[#1E63C4]/5 p-2.5" data-testid="paste-wanted-box">
                        <p className="text-xs text-[#555555] mb-1.5">One player per line: <code className="bg-white px-1 rounded">FirstName LastName Pts</code></p>
                        <textarea className={input} rows={4} value={wantPaste} onChange={(e) => setWantPaste(e.target.value)} placeholder={"Terry Wood 612\nSam Lee 540"} data-testid="paste-wanted-input" />
                        <div className="flex items-center gap-2 mt-1.5">
                          <button type="button" onClick={parseWantPaste} data-testid="paste-wanted-apply" className="inline-flex items-center gap-1 bg-[#1E63C4] text-white rounded-md px-3 py-1.5 text-xs font-semibold hover:bg-[#174e9c]">Replace rows with list</button>
                          <span className="text-xs text-[#999999]">First line = featured WANTED Player.</span>
                        </div>
                      </div>
                    )}
                    {wantRows().length > 0 && (
                      <div className="grid grid-cols-[1fr_6rem_auto] gap-1.5 mb-1 text-[11px] font-semibold text-[#555555] uppercase tracking-wide">
                        <span>Name</span><span>Score</span><span />
                      </div>
                    )}
                    <div className="space-y-1.5">
                      {wantRows().map((r, i) => (
                        <div key={r.id || i} className="grid grid-cols-[1fr_6rem_auto] gap-1.5 items-center" data-testid={`wanted-row-${i}`}>
                          <input className={`${input} ${i === 0 ? "border-amber-300" : ""}`} value={r.name} onChange={(e) => updWantRow(i, "name", e.target.value)} placeholder={i === 0 ? "WANTED Player name" : "Name"} data-testid={`wanted-name-${i}`} />
                          <input className={input} value={r.value} onChange={(e) => updWantRow(i, "value", e.target.value)} placeholder="Score" data-testid={`wanted-value-${i}`} />
                          <button type="button" onClick={() => rmWantRow(i)} className="text-red-500 p-1" data-testid={`remove-wanted-${i}`}><Trash2 className="h-4 w-4" /></button>
                        </div>
                      ))}
                      {wantRows().length === 0 && <p className="text-xs text-[#999999]">No qualifiers yet. Click Add.</p>}
                    </div>
                  </div>
                </div>
                </>)}
              </div>

              <div className="flex gap-3">
                <button onClick={saveDivision} data-testid="save-division-btn" className="inline-flex items-center gap-1 bg-[#1E63C4] text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-[#174e9c]">
                  <Save className="h-4 w-4" /> Save details
                </button>
                <button onClick={deleteDivision} data-testid="delete-division-btn" className="inline-flex items-center gap-1 text-red-600 rounded-md px-3 py-2 text-sm hover:bg-red-50 ml-auto">
                  <Trash2 className="h-4 w-4" /> Delete division
                </button>
              </div>
            </div>

            {/* FargoRate auto-sync */}
            <div className="bg-white rounded-lg border border-[#62A8E5]/40 p-6 space-y-4" data-testid="fargo-sync-panel">
              <div className="flex items-center gap-2">
                <RefreshCw className="h-5 w-5 text-[#1E63C4]" />
                <h3 className="font-heading font-bold text-lg">FargoRate Auto-Sync</h3>
              </div>
              <p className="text-sm text-[#555555] leading-relaxed">
                Paste this division&apos;s <strong>FargoRate Division ID</strong> to automatically pull its reports. It refreshes nightly at
                12&nbsp;AM, and you can sync instantly below. Find the ID in the FargoRate report URL (the
                <code className="bg-gray-100 px-1 rounded mx-1">divisionId=</code> value).
              </p>
              <input
                className={input}
                value={selected.fargo_division_id || ""}
                onChange={(e) => setSel("fargo_division_id", e.target.value)}
                placeholder="e.g. d46c00c0-9289-4b52-ad4c-b446010b2d66"
                data-testid="fargo-division-id-input"
              />
              <div>
                <p className="text-sm font-medium text-[#333333] mb-2">Reports to sync for this division</p>
                <div className="grid grid-cols-2 gap-2">
                  {ALL_REPORTS.map((r) => (
                    <label key={r.key} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedReports.includes(r.key)}
                        onChange={() => toggleReport(r.key)}
                        data-testid={`fargo-report-${r.key}`}
                        className="h-4 w-4 accent-[#1E63C4]"
                      />
                      {r.label}
                    </label>
                  ))}
                </div>
                <p className="text-xs text-[#555555] mt-1">Unchecked reports won&apos;t be created (and are removed on the next sync).</p>
                <div className="mt-2 flex gap-2 items-start text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2.5" data-testid="fargo-reports-warning">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
                  <span>Heads up: unchecking a report <strong>permanently removes its already-synced table</strong> from this division on the next sync (manual or nightly). Only the checked reports are kept.</span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={syncNow}
                  disabled={syncing}
                  data-testid="fargo-sync-now-btn"
                  className="inline-flex items-center gap-2 bg-[#1E63C4] text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-[#174e9c] disabled:opacity-60"
                >
                  <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync now"}
                </button>
                {selected.fargo_last_synced && (
                  <span className="text-xs text-[#555555]">Last synced: {new Date(selected.fargo_last_synced).toLocaleString()}</span>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <h3 className="font-heading font-bold text-lg">Tables (standings, stats, schedules…)</h3>
              <button onClick={addTable} data-testid="add-table-btn" className="inline-flex items-center gap-1 bg-[#E11D2A] text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-[#c41320]">
                <Plus className="h-4 w-4" /> Add table
              </button>
            </div>

            {tables.length === 0 ? (
              <p className="text-[#555555] text-sm">No tables yet. Click &quot;Add table&quot; then type data in or import a spreadsheet — or use FargoRate sync above.</p>
            ) : (
              <div className="space-y-6">
                {tables.map((t) => (
                  <TableEditor key={t.id} table={t} onSaved={reloadTables} onDeleted={reloadTables} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    </div>
  );
}
